import { useState } from "react";
import { useAtomValue } from "jotai";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import {
  buildCronExecution,
  buildCronSchedule,
  CronExecutionParseError,
  CronScheduleParseError,
  declaredConnectorScopeOf,
  ENTERPRISE_TASK_MAX_ATTEMPTS,
  type CronExecution,
  type CronSchedule,
  type CreateEnterpriseTaskInput,
  type EnterpriseTask,
  type EnterpriseTaskExecution,
  type EnterpriseTaskStatus,
  type EnterpriseTaskTarget,
} from "@dosia/shared";
import { apiCall } from "../../lib/api-client.js";
import { ApiError, canManageOrg } from "../../lib/api-types.js";
import type { PublicUser, UsersListResponse } from "../../lib/api-types.js";
import { authAtom } from "../../lib/auth-store.js";
import { cn } from "../../lib/cn.js";
import { useApiQuery } from "../../lib/useApiQuery.js";
import {
  tasksPath,
  taskByIdPath,
  taskRunNowPath,
  taskExecutionsPath,
  taskExecutionByIdPath,
  usersPath,
} from "../../lib/api-paths.js";
import { ErrorBanner } from "../../components/ErrorBanner.js";
import { Empty } from "../../components/Empty.js";
import { Modal } from "../../components/Modal.js";
import { Drawer } from "../../components/Drawer.js";
import { StatusPill } from "../../components/StatusPill.js";
import {
  FormField,
  PrimaryButton,
  SecondaryButton,
  Select,
  TextInput,
  TextArea,
} from "../../components/FormField.js";
import type { OrgDetailContext } from "./context.js";

/**
 * 自动化任务中心 — 企业管理员在管理后台创建/管理派发给员工 DOSIA 的定时任务。
 * dosia-enterprise 服务间通道透传（/tasks/*，org-admin + super/support 可管理）。业务单一真相源在
 * dosia-enterprise：本页只做列表渲染 + 建/改（乐观锁）+ 立即执行 + 真删 + 执行历史查看。
 *
 * 任务/执行的数据形状是跨端序列化结构（红线 #9），typed 定义在 @dosia/shared，
 * 本页 import 复用而非另declare。
 */

interface TasksListResponse {
  items: EnterpriseTask[];
  channelDisabled?: boolean;
}

interface ExecutionsListResponse {
  items: EnterpriseTaskExecution[];
  nextCursor?: string;
  channelDisabled?: boolean;
}

const TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

// ---------- 触发器 / 目标 / 状态 呈现 ----------

/** cron 表达式 → 人话摘要（覆盖常见形态，其余落到原样 `cron: <expr>`）。 */
function describeCron(expr: string): string {
  const parts = expr.trim().split(/\s+/);
  if (parts.length < 5) return `cron: ${expr}`;
  const [min, hr, dom, mon, dow] = parts;
  const isNum = (s: string): boolean => /^\d+$/.test(s);
  const hhmm = (h: string, m: string): string =>
    `${h.padStart(2, "0")}:${m.padStart(2, "0")}`;
  const WEEK = ["周日", "周一", "周二", "周三", "周四", "周五", "周六"];
  if (expr.trim() === "* * * * *") return "每分钟";
  if (isNum(min) && hr === "*" && dom === "*" && mon === "*" && dow === "*")
    return `每小时第 ${Number(min)} 分`;
  if (isNum(min) && isNum(hr) && dom === "*" && mon === "*" && dow === "*")
    return `每天 ${hhmm(hr, min)}`;
  if (isNum(min) && isNum(hr) && dom === "*" && mon === "*" && isNum(dow))
    return `每${WEEK[Number(dow) % 7] ?? `周${dow}`} ${hhmm(hr, min)}`;
  if (isNum(min) && isNum(hr) && isNum(dom) && mon === "*" && dow === "*")
    return `每月 ${Number(dom)} 日 ${hhmm(hr, min)}`;
  return `cron: ${expr}`;
}

/** 校验 cron 表达式：非空 + 5~6 段。返回 error 文案或 null。 */
function validateCron(expr: string): string | null {
  const trimmed = expr.trim();
  if (!trimmed) return "请填写 cron 表达式";
  const n = trimmed.split(/\s+/).length;
  if (n < 5 || n > 6) return "cron 表达式应为 5 段（分 时 日 月 周），可选 6 段带秒";
  return null;
}

function triggerSummary(trigger: CronSchedule): string {
  switch (trigger.kind) {
    case "cron":
      return describeCron(trigger.expr);
    case "every": {
      const mins = Math.round(trigger.everyMs / 60_000);
      if (mins >= 60 && mins % 60 === 0) return `每 ${mins / 60} 小时`;
      return `每 ${mins} 分钟`;
    }
    case "at":
      return `一次性 · ${formatTime(trigger.at)}`;
  }
}

function targetSummary(target: EnterpriseTaskTarget): string {
  return target.kind === "all" ? "全员" : `${target.userIds.length} 人`;
}

function executionKindSummary(execution: CronExecution | undefined): string {
  switch (execution?.kind) {
    case "agentTurn":
      return "完整 AI 任务（agentTurn）";
    case "modelCall":
      return "单次模型调用（modelCall）";
    case "script":
      return "脚本（script）";
    case "systemEvent":
      return "系统事件（systemEvent）";
    case "loopWakeup":
      return "循环唤醒（loopWakeup）";
    default:
      return "旧式完整 AI 任务";
  }
}

/**
 * 连接器摘要。「有没有声明面」取 shared 的 `declaredConnectorScopeOf`，与审批卡
 * （normalize-task-submission.ts 的 resolveCardMcpScope）、审计轨（task-actions.ts
 * 的 auditMcpScope）同一条判据。
 *
 * 此前这里写 `kind !== "agentTurn" → "不适用"`，而**本面板自己**就在给 script /
 * modelCall 写 `mcpServers`（见下方 buildCronExecution 的两处 `input.mcpServers`）：
 * 管理员在这个面板建一条点名 catalyst-juguang 的脚本任务，建完那一行立刻显示
 * 「连接器：不适用」。与审批卡同一个方向的低报，面向的还是同一个人。
 */
function connectorSummary(execution: CronExecution | undefined): string {
  if (!execution) return "旧式未声明";
  const declared = declaredConnectorScopeOf(execution);
  if (!declared.declarable) return "不适用";
  const scope = declared.scope;
  if (scope === "all") return "全部可用";
  // agentTurn 的缺省 = 旧式全量工具面（最宽）；script / modelCall 的缺省 = 契约明写的
  // 「no bridge at all」（最窄，与 `[]` 同档）。两者不可用同一句话呈现。
  if (!scope) return execution.kind === "agentTurn" ? "旧式未声明" : "无外部连接器";
  if (scope.length === 0) return "无外部连接器";
  return scope.join("、");
}

/** 应用工具面摘要。三态不可塌陷：字段缺失 = 旧式全量，`[]` = 显式一个都不给。
 *  这里的 `kind !== "agentTurn"` 与 connectorSummary 不同类，是对的：script /
 *  modelCall 的契约里确实没有 `dosiaTools` 字段，「不适用」是诚实的。 */
function appToolSummary(execution: CronExecution | undefined): string {
  if (!execution) return "旧式全量";
  if (execution.kind !== "agentTurn") return "不适用";
  if (!execution.dosiaTools) return "旧式全量";
  if (execution.dosiaTools.length === 0) return "仅文件操作";
  return execution.dosiaTools.join("、");
}

function budgetSummary(execution: CronExecution | undefined): string {
  if (!execution || !("maxBudgetUsd" in execution) || execution.maxBudgetUsd === undefined) {
    return "不限制";
  }
  return `$${execution.maxBudgetUsd}`;
}

type ExecutionKind = "device" | "agentTurn" | "modelCall" | "script";
type TriggerKind = CronSchedule["kind"];
type BridgeConnectorMode = "none" | "named";
type ConnectorPolicyChoice = "required" | "optional";

function isServerExecutionKind(kind: string | undefined): kind is "agentTurn" | "modelCall" | "script" {
  return kind === "agentTurn" || kind === "modelCall" || kind === "script";
}

function isDesktopOnlyKind(
  kind: CronExecution["kind"] | undefined,
): kind is "systemEvent" | "loopWakeup" {
  return kind === "systemEvent" || kind === "loopWakeup";
}

function initialExecutionKind(editing: EnterpriseTask | null): ExecutionKind {
  if (!editing) return "device";
  const kind = editing.execution?.kind;
  return isServerExecutionKind(kind) ? kind : "device";
}

function parseNamedList(raw: string): string[] {
  return raw.split(",").map((name) => name.trim()).filter(Boolean);
}

function parseOptionalPositiveNumber(
  raw: string,
): { ok: true; value: number | null } | { ok: false } {
  const trimmed = raw.trim();
  if (!trimmed) return { ok: true, value: null };
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n <= 0) return { ok: false };
  return { ok: true, value: n };
}

function toDatetimeLocalValue(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number): string => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** 排期三格的初始值：编辑时由原排期推出，新建时取默认值。 */
interface ScheduleFormValues {
  cronExpr: string;
  everyMinutes: string;
  atLocal: string;
}

function scheduleFormValues(trigger: CronSchedule | undefined): ScheduleFormValues {
  return {
    cronExpr: trigger?.kind === "cron" ? trigger.expr : "0 9 * * 1",
    everyMinutes:
      trigger?.kind === "every"
        ? String(Math.max(1, Math.round(trigger.everyMs / 60_000)))
        : "30",
    atLocal: trigger?.kind === "at" ? toDatetimeLocalValue(trigger.at) : "",
  };
}

/**
 * 编辑时排期那一格没动过 → 原排期逐字返回；动过（或换了触发方式）→ null，由调用方按表单重建（B-254）。
 *
 * 服务端按值比排期（apps/web/server/enterprise/task-schedule-floor.ts `nextScheduleFloorAt`）：
 * 回传的排期哪怕只差一处都算「改了时间」。而表单装不下原排期的全部信息——every 的锚点、
 * 不是整分钟的间隔、一次性时刻的秒、cron 的时区都不在表单里——照表单重建，只改任务名也会
 * 让 every 的运行网格挪到保存那一刻、已跑完的一次性任务再跑一次、cron 换成浏览器时区。
 * 「没动过」按表单初始值比：间隔比分钟数，时刻比选择器的串，表达式比去掉首尾空白后的串。
 */
function untouchedSchedule(
  original: CronSchedule | undefined,
  kind: TriggerKind,
  form: ScheduleFormValues,
): CronSchedule | null {
  if (!original || original.kind !== kind) return null;
  const initial = scheduleFormValues(original);
  switch (kind) {
    case "cron":
      return form.cronExpr.trim() === initial.cronExpr.trim() ? original : null;
    case "every":
      return Number(form.everyMinutes) === Number(initial.everyMinutes) ? original : null;
    case "at":
      return form.atLocal === initial.atLocal ? original : null;
  }
}

function formatSaveError(err: unknown): string {
  if (err instanceof CronExecutionParseError || err instanceof CronScheduleParseError) {
    return err.message;
  }
  if (!(err instanceof ApiError)) return "保存失败";
  if (err.status === 409) return "该任务已被他处修改，请关闭后刷新再试。";
  // 契约级 400（framework 缺失、mcpServers 未声明、命令吞退出码…）的判定权全在服务端：
  // task-actions 的 failWith 发 `{ success:false, error:{ message } }`，admin-portal 代理
  // 原样透传（unwrapServiceEnvelope 只拆 success===true），apiCall 把 message 取到 err.message。
  // 所以这里直接展示它，前端不复刻任何一条契约规则——复刻出来的副本必然和服务端漂移。
  return err.message;
}

const STATUS_TONE: Record<
  EnterpriseTaskStatus,
  { tone: "active" | "suspended" | "archived"; label: string }
> = {
  active: { tone: "active", label: "启用中" },
  paused: { tone: "suspended", label: "已暂停" },
  // 状态值是 archived，界面一律叫「下线」，和按钮、员工桌面的「下线」同一个词（B-342）。
  archived: { tone: "archived", label: "已下线" },
};

function formatTime(iso?: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ---------- 建/改任务表单 ----------

type DialogState =
  | { mode: "create" }
  | { mode: "edit"; task: EnterpriseTask };

interface TaskFormDialogProps {
  orgCode: string;
  token: string;
  state: DialogState;
  onClose: () => void;
  onSaved: () => void;
}

function BridgeConnectorFields({
  mode,
  names,
  policy,
  hint,
  onModeChange,
  onNamesChange,
  onPolicyChange,
}: {
  mode: BridgeConnectorMode;
  names: string;
  policy: ConnectorPolicyChoice;
  hint: string;
  onModeChange: (mode: BridgeConnectorMode) => void;
  onNamesChange: (names: string) => void;
  onPolicyChange: (policy: ConnectorPolicyChoice) => void;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <>
      <FormField label={t("admin.tasks.execution.connectorLabel")} hint={hint}>
        <div className="space-y-2">
          <Select
            aria-label={t("admin.tasks.execution.connectorLabel")}
            value={mode}
            onChange={(event) => {
              const value = event.target.value;
              if (value === "none" || value === "named") onModeChange(value);
            }}
          >
            <option value="none">{t("admin.tasks.execution.scriptConnectorNone")}</option>
            <option value="named">{t("admin.tasks.execution.connector.named")}</option>
          </Select>
          {mode === "named" && (
            <TextInput
              aria-label={t("admin.tasks.execution.namedConnectors")}
              value={names}
              onChange={(event) => onNamesChange(event.target.value)}
              placeholder="例：feishu, catalyst-juguang"
            />
          )}
        </div>
      </FormField>
      {mode === "named" && (
        <FormField label={t("admin.tasks.execution.connectorPolicy")}>
          <Select
            aria-label={t("admin.tasks.execution.connectorPolicy")}
            value={policy}
            onChange={(event) => {
              const value = event.target.value;
              if (value === "required" || value === "optional") onPolicyChange(value);
            }}
          >
            <option value="required">{t("admin.tasks.execution.connectorPolicy.required")}</option>
            <option value="optional">{t("admin.tasks.execution.connectorPolicy.optional")}</option>
          </Select>
        </FormField>
      )}
    </>
  );
}

function TaskFormDialog({
  orgCode,
  token,
  state,
  onClose,
  onSaved,
}: TaskFormDialogProps): JSX.Element {
  const { t } = useTranslation();
  const editing = state.mode === "edit" ? state.task : null;
  const agentTurnExecution = editing?.execution?.kind === "agentTurn" ? editing.execution : null;
  const scriptExecution = editing?.execution?.kind === "script" ? editing.execution : null;
  const modelCallExecution = editing?.execution?.kind === "modelCall" ? editing.execution : null;
  const desktopOnly = isDesktopOnlyKind(editing?.execution?.kind);
  const [title, setTitle] = useState(editing?.title ?? "");
  // agentTurn 的真正执行 prompt 在 execution.prompt；顶层 prompt 只是旧展示面。
  // 编辑时以执行值为准，并在保存时同步两处，彻底消除“看似改了、实际没改”。
  const [prompt, setPrompt] = useState(agentTurnExecution?.prompt ?? editing?.prompt ?? "");
  const [executionKind, setExecutionKind] = useState<ExecutionKind>(() =>
    initialExecutionKind(editing),
  );
  const [framework, setFramework] = useState<"" | "claude" | "pi">(
    agentTurnExecution?.framework === "claude" || agentTurnExecution?.framework === "pi"
      ? agentTurnExecution.framework
      : "",
  );
  const [connectorMode, setConnectorMode] = useState<"undeclared" | "none" | "all" | "named">(() => {
    if (!editing) return "none";
    if (!agentTurnExecution?.mcpServers) return "undeclared";
    if (agentTurnExecution.mcpServers.length === 0) return "none";
    return agentTurnExecution.mcpServers === "all" ? "all" : "named";
  });
  const [connectorNames, setConnectorNames] = useState(
    Array.isArray(agentTurnExecution?.mcpServers)
      ? agentTurnExecution.mcpServers.join(", ")
      : "",
  );
  // 应用工具面（dosiaTools）——与 mcpServers 并列的另一条工具轴。契约层三态语义
  // 不可塌陷：undefined = 旧式全量、[] = 显式一个都不给、[…] = 点名。这里没有
  // 「未声明」选项：契约允许 undefined，但既然管理员已经打开这个表单，让他在
  // 「全部/无/点名」里明选一个更诚实；保存时 all 档写回 undefined，行为与旧任务
  // 完全一致，不给存量任务制造语义变更。
  const [appToolMode, setAppToolMode] = useState<"all" | "none" | "named">(() => {
    if (!agentTurnExecution?.dosiaTools) return "all";
    return agentTurnExecution.dosiaTools.length === 0 ? "none" : "named";
  });
  const [appToolNames, setAppToolNames] = useState(
    agentTurnExecution?.dosiaTools?.join(", ") ?? "",
  );
  const [maxBudgetUsd, setMaxBudgetUsd] = useState(
    agentTurnExecution?.maxBudgetUsd?.toString() ?? "",
  );
  const [agentModel, setAgentModel] = useState(agentTurnExecution?.model ?? "");
  const [agentTimeoutSeconds, setAgentTimeoutSeconds] = useState(
    agentTurnExecution?.timeoutSeconds?.toString() ?? "",
  );
  // B-124「先检查（不花钱）」：完整 AI 任务的零模型前置检查命令。留空 = 不检查（每轮都唤醒 AI）。
  const [precheckCommand, setPrecheckCommand] = useState(agentTurnExecution?.precheck?.command ?? "");
  const [scriptCommand, setScriptCommand] = useState(scriptExecution?.command ?? "");
  const [scriptCwd, setScriptCwd] = useState(scriptExecution?.cwd ?? "");
  const [scriptTimeoutMs, setScriptTimeoutMs] = useState(
    scriptExecution?.timeoutMs?.toString() ?? "",
  );
  const scriptServers = scriptExecution?.mcpServers;
  const [scriptConnectorMode, setScriptConnectorMode] = useState<BridgeConnectorMode>(
    Array.isArray(scriptServers) && scriptServers.length > 0 ? "named" : "none",
  );
  const [scriptConnectorNames, setScriptConnectorNames] = useState(
    Array.isArray(scriptServers) ? scriptServers.join(", ") : "",
  );
  const [scriptConnectorPolicy, setScriptConnectorPolicy] = useState<ConnectorPolicyChoice>(
    scriptExecution?.connectorPolicy === "optional" ? "optional" : "required",
  );
  const [systemPrompt, setSystemPrompt] = useState(modelCallExecution?.systemPrompt ?? "");
  const [promptTemplate, setPromptTemplate] = useState(modelCallExecution?.promptTemplate ?? "");
  const [prefetchCommand, setPrefetchCommand] = useState(modelCallExecution?.command ?? "");
  const [modelCallModel, setModelCallModel] = useState(modelCallExecution?.model ?? "");
  const [modelCallBudget, setModelCallBudget] = useState(
    modelCallExecution?.maxBudgetUsd?.toString() ?? "",
  );
  const modelCallServers = modelCallExecution?.mcpServers;
  const [modelCallConnectorMode, setModelCallConnectorMode] = useState<BridgeConnectorMode>(
    Array.isArray(modelCallServers) && modelCallServers.length > 0 ? "named" : "none",
  );
  const [modelCallConnectorNames, setModelCallConnectorNames] = useState(
    Array.isArray(modelCallServers) ? modelCallServers.join(", ") : "",
  );
  const [modelCallConnectorPolicy, setModelCallConnectorPolicy] = useState<ConnectorPolicyChoice>(
    modelCallExecution?.connectorPolicy === "optional" ? "optional" : "required",
  );
  const [triggerKind, setTriggerKind] = useState<TriggerKind>(editing?.trigger.kind ?? "cron");
  const initialSchedule = scheduleFormValues(editing?.trigger);
  const [cronExpr, setCronExpr] = useState(initialSchedule.cronExpr);
  const [everyMinutes, setEveryMinutes] = useState(initialSchedule.everyMinutes);
  const [atLocal, setAtLocal] = useState(initialSchedule.atLocal);
  const [targetKind, setTargetKind] = useState<"all" | "users">(
    editing?.target.kind ?? "all",
  );
  const [selectedUsers, setSelectedUsers] = useState<string[]>(
    editing?.target.kind === "users" ? editing.target.userIds : [],
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 只在选「具体员工」时拉员工列表（省一次请求；测试仅当选人才 mock users）。
  const usersQ = useApiQuery<UsersListResponse>(
    targetKind === "users" ? usersPath(orgCode) : null,
    [orgCode, targetKind],
  );

  const cronErr = triggerKind === "cron" ? validateCron(cronExpr) : null;
  const keptTrigger = untouchedSchedule(editing?.trigger, triggerKind, {
    cronExpr,
    everyMinutes,
    atLocal,
  });
  // 原样保留的 cron 按它自己的时区跑，提示照写；没带时区的跑在服务器时区，提示仍写浏览器时区（与修前同）。
  const cronTz = keptTrigger?.kind === "cron" && keptTrigger.tz ? keptTrigger.tz : TZ;
  const agentBudgetParsed = parseOptionalPositiveNumber(maxBudgetUsd);
  const modelBudgetParsed = parseOptionalPositiveNumber(modelCallBudget);
  const agentTimeoutParsed = parseOptionalPositiveNumber(agentTimeoutSeconds);
  const scriptTimeoutParsed = parseOptionalPositiveNumber(scriptTimeoutMs);
  const BUDGET_ERR = "单次任务执行封顶必须是大于 0 的 USD 数值，留空表示不限制";
  const budgetErr =
    executionKind === "agentTurn" && framework !== "pi" && !agentBudgetParsed.ok
      ? BUDGET_ERR
      : executionKind === "modelCall" && !modelBudgetParsed.ok
        ? BUDGET_ERR
        : null;

  const toggleUser = (user: string): void => {
    setSelectedUsers((prev) =>
      prev.includes(user) ? prev.filter((u) => u !== user) : [...prev, user],
    );
  };

  const buildTarget = (): EnterpriseTaskTarget | null => {
    if (targetKind === "all") return { kind: "all" };
    if (selectedUsers.length === 0) return null;
    return { kind: "users", userIds: selectedUsers };
  };

  const submit = async (): Promise<void> => {
    if (!title.trim()) {
      setError("请填写标题");
      return;
    }
    if (!prompt.trim()) {
      setError("请填写任务描述");
      return;
    }
    if (triggerKind === "cron" && cronErr) {
      setError(cronErr);
      return;
    }
    if (triggerKind === "every") {
      const mins = Number(everyMinutes);
      if (!Number.isFinite(mins) || mins <= 0) {
        setError(t("admin.tasks.trigger.everyRequired"));
        return;
      }
    }
    if (triggerKind === "at") {
      if (!atLocal.trim() || !Number.isFinite(new Date(atLocal).getTime())) {
        setError(t("admin.tasks.trigger.atRequired"));
        return;
      }
    }
    if (budgetErr) {
      setError(budgetErr);
      return;
    }
    const namedConnectors = parseNamedList(connectorNames);
    if (executionKind === "agentTurn" && connectorMode === "undeclared") {
      setError("该旧任务未声明外部连接器范围；保存前请明确选择无、全部或指定连接器");
      return;
    }
    if (executionKind === "agentTurn" && connectorMode === "named" && namedConnectors.length === 0) {
      setError("请填写至少一个外部连接器名称，或选择「无外部连接器」");
      return;
    }
    const namedAppTools = parseNamedList(appToolNames);
    if (executionKind === "agentTurn" && appToolMode === "named" && namedAppTools.length === 0) {
      setError("请填写至少一个 DOSIA 应用工具名称，或选择「全部可用」/「仅文件操作」");
      return;
    }
    if (
      executionKind === "agentTurn" &&
      !editing &&
      framework !== "claude" &&
      framework !== "pi"
    ) {
      setError(t("admin.tasks.execution.frameworkRequired"));
      return;
    }
    if (executionKind === "agentTurn" && !agentTimeoutParsed.ok) {
      setError(t("admin.tasks.execution.timeoutInvalid"));
      return;
    }
    if (executionKind === "script" && !scriptCommand.trim()) {
      setError(t("admin.tasks.execution.commandRequired"));
      return;
    }
    if (executionKind === "script" && !scriptTimeoutParsed.ok) {
      setError(t("admin.tasks.execution.timeoutInvalid"));
      return;
    }
    const scriptNamed = parseNamedList(scriptConnectorNames);
    if (executionKind === "script" && scriptConnectorMode === "named" && scriptNamed.length === 0) {
      setError(t("admin.tasks.execution.namedConnectorsRequired"));
      return;
    }
    if (executionKind === "modelCall" && !systemPrompt.trim()) {
      setError(t("admin.tasks.execution.systemPromptRequired"));
      return;
    }
    if (executionKind === "modelCall" && !promptTemplate.trim()) {
      setError(t("admin.tasks.execution.promptTemplateRequired"));
      return;
    }
    const modelNamed = parseNamedList(modelCallConnectorNames);
    if (
      executionKind === "modelCall" &&
      modelCallConnectorMode === "named" &&
      modelNamed.length === 0
    ) {
      setError(t("admin.tasks.execution.namedConnectorsRequired"));
      return;
    }
    const target = buildTarget();
    if (!target) {
      setError("请至少选择一个员工，或切换为「全员」");
      return;
    }
    let trigger: CronSchedule;
    let execution: CronExecution | undefined;
    try {
      if (keptTrigger) {
        trigger = keptTrigger;
      } else if (triggerKind === "cron") {
        trigger = buildCronSchedule({ kind: "cron", expr: cronExpr.trim(), tz: TZ });
      } else if (triggerKind === "every") {
        trigger = buildCronSchedule({
          kind: "every",
          everyMs: Number(everyMinutes) * 60_000,
        });
      } else {
        trigger = buildCronSchedule({ kind: "at", at: new Date(atLocal).toISOString() });
      }
      if (!desktopOnly && executionKind === "agentTurn") {
        // 先剥掉旧 maxBudgetUsd / dosiaTools：空输入必须真正删掉属性，
        // 「全部可用」必须写回缺席而不是空数组（三态不可塌陷）。
        const input: Record<string, unknown> = {
          kind: "agentTurn",
          prompt: prompt.trim(),
          mcpServers:
            connectorMode === "all" ? "all" : connectorMode === "none" ? [] : namedConnectors,
        };
        if (agentModel.trim()) input.model = agentModel.trim();
        if (framework === "claude" || framework === "pi") input.framework = framework;
        if (agentTurnExecution?.agent) input.agent = agentTurnExecution.agent;
        if (agentTurnExecution?.agentPolicy) input.agentPolicy = agentTurnExecution.agentPolicy;
        if (connectorMode === "all") {
          input.connectorPolicy = "required";
        } else if (connectorMode !== "none" && agentTurnExecution?.connectorPolicy) {
          input.connectorPolicy = agentTurnExecution.connectorPolicy;
        }
        if (agentTimeoutParsed.ok && agentTimeoutParsed.value !== null) {
          input.timeoutSeconds = agentTimeoutParsed.value;
        }
        // 「先检查（不花钱）」：填了才写；编辑时保留原有的 cwd / 时限（表单只露命令这一栏）。留空 = 去掉。
        if (precheckCommand.trim()) {
          input.precheck = { ...(agentTurnExecution?.precheck ?? {}), command: precheckCommand.trim() };
        }
        // all = 不写这个字段（旧式全量），与存量任务行为逐字一致。
        if (appToolMode !== "all") {
          input.dosiaTools = appToolMode === "none" ? [] : namedAppTools;
        }
        if (
          framework !== "pi" &&
          agentBudgetParsed.ok &&
          agentBudgetParsed.value !== null
        ) {
          input.maxBudgetUsd = agentBudgetParsed.value;
        }
        execution = buildCronExecution(input);
      } else if (!desktopOnly && executionKind === "script") {
        const input: Record<string, unknown> = {
          kind: "script",
          command: scriptCommand.trim(),
        };
        if (scriptCwd.trim()) input.cwd = scriptCwd.trim();
        if (scriptTimeoutParsed.ok && scriptTimeoutParsed.value !== null) {
          input.timeoutMs = scriptTimeoutParsed.value;
        }
        if (scriptConnectorMode === "named") {
          input.mcpServers = scriptNamed;
          input.connectorPolicy = scriptConnectorPolicy;
        }
        execution = buildCronExecution(input);
      } else if (!desktopOnly && executionKind === "modelCall") {
        const input: Record<string, unknown> = {
          kind: "modelCall",
          systemPrompt: systemPrompt.trim(),
          promptTemplate: promptTemplate.trim(),
        };
        if (prefetchCommand.trim()) input.command = prefetchCommand.trim();
        if (modelCallModel.trim()) input.model = modelCallModel.trim();
        if (modelBudgetParsed.ok && modelBudgetParsed.value !== null) {
          input.maxBudgetUsd = modelBudgetParsed.value;
        }
        if (modelCallConnectorMode === "named") {
          input.mcpServers = modelNamed;
          input.connectorPolicy = modelCallConnectorPolicy;
        }
        execution = buildCronExecution(input);
      }
    } catch (err) {
      setError(formatSaveError(err));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      if (editing) {
        await apiCall("PATCH", taskByIdPath(orgCode, editing.taskId), {
          token,
          body: {
            title: title.trim(),
            prompt: prompt.trim(),
            trigger,
            target,
            ...(execution ? { execution } : {}),
            expectedVersion: editing.version,
          },
        });
      } else {
        const body: CreateEnterpriseTaskInput = {
          title: title.trim(),
          prompt: prompt.trim(),
          trigger,
          target,
          ...(execution ? { execution } : {}),
        };
        await apiCall("POST", tasksPath(orgCode), { token, body });
      }
      onSaved();
    } catch (err) {
      setError(formatSaveError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={editing ? "编辑任务" : "新建自动化任务"}
      size="lg"
      footer={
        <>
          <SecondaryButton disabled={submitting} onClick={onClose}>
            取消
          </SecondaryButton>
          <PrimaryButton disabled={submitting} onClick={() => void submit()}>
            {submitting ? "保存中…" : editing ? "保存" : "创建"}
          </PrimaryButton>
        </>
      }
    >
      <div className="space-y-4">
        {error && <ErrorBanner onDismiss={() => setError(null)}>{error}</ErrorBanner>}
        <FormField label="标题" required>
          <TextInput
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="例：妙记自动归档"
          />
        </FormField>
        {!editing && (
          <fieldset className="space-y-2">
            <legend className="text-sm text-dosia-text-secondary">
              {t("admin.tasks.execution.whereLabel")}
              <span className="ml-0.5 text-amber-700">*</span>
            </legend>
            <div className="grid grid-cols-2 gap-2">
              <label
                className={cn(
                  "flex cursor-pointer flex-col gap-1 rounded-md border px-3 py-2",
                  executionKind === "device"
                    ? "border-dosia-accent bg-dosia-accent/10"
                    : "border-dosia-border bg-dosia-bg-lighter",
                )}
              >
                <span className="flex items-center gap-1.5 text-sm font-medium">
                  <input
                    type="radio"
                    name="task-execution-location"
                    aria-label={t("admin.tasks.execution.deviceTitle")}
                    checked={executionKind === "device"}
                    onChange={() => setExecutionKind("device")}
                    className="accent-dosia-accent"
                  />
                  {t("admin.tasks.execution.deviceTitle")}
                </span>
                <span className="text-xs text-dosia-text-dim">
                  {t("admin.tasks.execution.deviceHint")}
                </span>
              </label>
              <label
                className={cn(
                  "flex cursor-pointer flex-col gap-1 rounded-md border px-3 py-2",
                  executionKind !== "device"
                    ? "border-dosia-accent bg-dosia-accent/10"
                    : "border-dosia-border bg-dosia-bg-lighter",
                )}
              >
                <span className="flex items-center gap-1.5 text-sm font-medium">
                  <input
                    type="radio"
                    name="task-execution-location"
                    aria-label={t("admin.tasks.execution.serverTitle")}
                    checked={executionKind !== "device"}
                    onChange={() => {
                      if (executionKind === "device") setExecutionKind("agentTurn");
                    }}
                    className="accent-dosia-accent"
                  />
                  {t("admin.tasks.execution.serverTitle")}
                </span>
                <span className="text-xs text-dosia-text-dim">
                  {t("admin.tasks.execution.serverHint")}
                </span>
              </label>
            </div>
          </fieldset>
        )}
        {editing && desktopOnly && (
          <FormField label="执行类型">
            <div className="rounded-md border border-dosia-border bg-dosia-bg-lighter px-2 py-1.5 text-sm">
              {executionKindSummary(editing.execution)}
            </div>
          </FormField>
        )}
        {editing && !desktopOnly && (
          <FormField
            label={t("admin.tasks.execution.whereLabel")}
            hint={t("admin.tasks.execution.editLockedHint")}
          >
            <div className="rounded-md border border-dosia-border bg-dosia-bg-lighter px-2 py-1.5 text-sm">
              {executionKind === "device"
                ? t("admin.tasks.execution.editDeviceSummary")
                : t("admin.tasks.execution.editServerSummary", {
                    kind: t(`admin.tasks.execution.kind.${executionKind}`),
                  })}
            </div>
          </FormField>
        )}
        {!editing && executionKind !== "device" && (
          <FormField
            label={t("admin.tasks.execution.serverKindLabel")}
            required
            hint={t("admin.tasks.execution.serverKindHint")}
          >
            <Select
              aria-label={t("admin.tasks.execution.serverKindLabel")}
              value={executionKind}
              onChange={(event) => {
                const value = event.target.value;
                if (value === "agentTurn" || value === "modelCall" || value === "script") {
                  setExecutionKind(value);
                }
              }}
            >
              <option value="agentTurn">{t("admin.tasks.execution.kind.agentTurn")}</option>
              <option value="modelCall">{t("admin.tasks.execution.kind.modelCall")}</option>
              <option value="script">{t("admin.tasks.execution.kind.script")}</option>
            </Select>
          </FormField>
        )}
        <FormField
          label="任务描述"
          required
          hint={
            executionKind === "agentTurn"
              ? "这是完整 AI 任务真正执行的 prompt；保存时会同步更新展示描述与 execution.prompt。"
              : desktopOnly
                ? "这是任务的展示描述；当前执行配置为只读，下面会显示真实执行类型。"
                : executionKind === "device"
                  ? "自由 prompt — 员工 DOSIA 拿这段 + 全套工具自动执行"
                  : t("admin.tasks.execution.displayPromptHint")
          }
        >
          <TextArea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={6}
            placeholder="例：用 lark-cli 拉本周所有妙记，逐条导出文字稿写到个人会议目录…"
          />
        </FormField>
        {executionKind === "agentTurn" && (
          <>
            <FormField
              label={t("admin.tasks.execution.frameworkLabel")}
              required={!editing}
              hint={t("admin.tasks.execution.frameworkHint")}
            >
              <Select
                aria-label={t("admin.tasks.execution.frameworkLabel")}
                value={framework}
                onChange={(event) => {
                  const value = event.target.value;
                  if (value === "" || value === "claude" || value === "pi") setFramework(value);
                }}
              >
                <option value="">{t("admin.tasks.execution.frameworkPlaceholder")}</option>
                <option value="claude">{t("admin.tasks.execution.framework.claude")}</option>
                <option value="pi">{t("admin.tasks.execution.framework.pi")}</option>
              </Select>
            </FormField>
            <FormField
              label="外部连接器"
              hint="无外部连接器 = 仅内置工具；全部可用 = 挂载工作区全部已授权连接器；指定名称用英文逗号分隔。"
            >
              <div className="space-y-2">
                <Select
                  aria-label="外部连接器"
                  value={connectorMode}
                  onChange={(event) => {
                    const value = event.target.value;
                    if (
                      value === "undeclared" ||
                      value === "none" ||
                      value === "all" ||
                      value === "named"
                    ) {
                      setConnectorMode(value);
                    }
                  }}
                >
                  {editing && (
                    <option value="undeclared" disabled>旧式未声明（保存前请选择）</option>
                  )}
                  <option value="none">无外部连接器</option>
                  <option value="all">全部可用</option>
                  <option value="named">指定名称</option>
                </Select>
                {connectorMode === "named" && (
                  <TextInput
                    aria-label="指定外部连接器名称"
                    value={connectorNames}
                    onChange={(event) => setConnectorNames(event.target.value)}
                    placeholder="例：feishu, catalyst-juguang"
                  />
                )}
              </div>
            </FormField>
            <FormField
              label={t("admin.tasks.execution.precheckCommand")}
              hint={t("admin.tasks.execution.precheckCommandHint")}
            >
              <TextInput
                aria-label={t("admin.tasks.execution.precheckCommand")}
                value={precheckCommand}
                onChange={(event) => setPrecheckCommand(event.target.value)}
                placeholder={t("admin.tasks.execution.precheckCommandPlaceholder")}
                className="font-mono"
              />
            </FormField>
            <details className="rounded-md border border-dosia-border px-3 py-2">
              <summary className="cursor-pointer select-none text-sm text-dosia-text-secondary">
                {t("admin.tasks.execution.moreSettings.agentTurn")}
              </summary>
              <div className="mt-3 space-y-4">
                <FormField
                  label="DOSIA 应用工具"
                  hint="与外部连接器并列的另一条工具面。全部可用 = 该身份能用的全部应用工具（旧任务行为）；仅文件操作 = 一个应用工具都不给（SDK 原生读写文件仍可用）；指定名称用英文逗号分隔，例如只给发布网页任务 publish_artifact。"
                >
                  <div className="space-y-2">
                    <Select
                      aria-label="DOSIA 应用工具"
                      value={appToolMode}
                      onChange={(event) => {
                        const value = event.target.value;
                        if (value === "all" || value === "none" || value === "named") {
                          setAppToolMode(value);
                        }
                      }}
                    >
                      <option value="all">全部可用</option>
                      <option value="none">仅文件操作（不给应用工具）</option>
                      <option value="named">指定名称</option>
                    </Select>
                    {appToolMode === "named" && (
                      <TextInput
                        aria-label="指定 DOSIA 应用工具名称"
                        value={appToolNames}
                        onChange={(event) => setAppToolNames(event.target.value)}
                        placeholder="例：publish_artifact, web_search"
                      />
                    )}
                  </div>
                </FormField>
                <FormField label={t("admin.tasks.execution.timeoutSeconds")}>
                  <TextInput
                    aria-label={t("admin.tasks.execution.timeoutSeconds")}
                    type="number"
                    min="1"
                    step="1"
                    value={agentTimeoutSeconds}
                    onChange={(event) => setAgentTimeoutSeconds(event.target.value)}
                  />
                </FormField>
                <FormField
                  label={t("admin.tasks.execution.model")}
                  hint={t("admin.tasks.execution.modelHint")}
                >
                  <TextInput
                    aria-label={t("admin.tasks.execution.model")}
                    value={agentModel}
                    onChange={(event) => setAgentModel(event.target.value)}
                  />
                </FormField>
                {framework !== "pi" && (
                  <FormField
                    label={t("admin.tasks.execution.maxBudgetUsd")}
                    error={budgetErr ?? undefined}
                    hint={t("admin.tasks.execution.maxBudgetUsdHint")}
                  >
                    <TextInput
                      aria-label={t("admin.tasks.execution.maxBudgetUsd")}
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={maxBudgetUsd}
                      onChange={(event) => setMaxBudgetUsd(event.target.value)}
                      placeholder="留空 = 不限制"
                    />
                  </FormField>
                )}
              </div>
            </details>
          </>
        )}
        {executionKind === "modelCall" && (
          <>
            <FormField label={t("admin.tasks.execution.systemPrompt")} required>
              <TextArea
                aria-label={t("admin.tasks.execution.systemPrompt")}
                value={systemPrompt}
                onChange={(event) => setSystemPrompt(event.target.value)}
                rows={4}
              />
            </FormField>
            <FormField label={t("admin.tasks.execution.promptTemplate")} required>
              <TextArea
                aria-label={t("admin.tasks.execution.promptTemplate")}
                value={promptTemplate}
                onChange={(event) => setPromptTemplate(event.target.value)}
                rows={4}
              />
            </FormField>
            <details className="rounded-md border border-dosia-border px-3 py-2">
              <summary className="cursor-pointer select-none text-sm text-dosia-text-secondary">
                {t("admin.tasks.execution.moreSettings.modelCall")}
              </summary>
              <div className="mt-3 space-y-4">
                <FormField
                  label={t("admin.tasks.execution.model")}
                  hint={t("admin.tasks.execution.modelHint")}
                >
                  <TextInput
                    aria-label={t("admin.tasks.execution.model")}
                    value={modelCallModel}
                    onChange={(event) => setModelCallModel(event.target.value)}
                  />
                </FormField>
                <FormField label={t("admin.tasks.execution.prefetchCommand")}>
                  <TextInput
                    aria-label={t("admin.tasks.execution.prefetchCommand")}
                    value={prefetchCommand}
                    onChange={(event) => setPrefetchCommand(event.target.value)}
                    className="font-mono"
                  />
                </FormField>
                <BridgeConnectorFields
                  mode={modelCallConnectorMode}
                  names={modelCallConnectorNames}
                  policy={modelCallConnectorPolicy}
                  hint={t("admin.tasks.execution.modelCallConnectorHint")}
                  onModeChange={setModelCallConnectorMode}
                  onNamesChange={setModelCallConnectorNames}
                  onPolicyChange={setModelCallConnectorPolicy}
                />
                <FormField
                  label={t("admin.tasks.execution.maxBudgetUsd")}
                  error={budgetErr ?? undefined}
                  hint={t("admin.tasks.execution.maxBudgetUsdHint")}
                >
                  <TextInput
                    aria-label={t("admin.tasks.execution.maxBudgetUsd")}
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={modelCallBudget}
                    onChange={(event) => setModelCallBudget(event.target.value)}
                    placeholder="留空 = 不限制"
                  />
                </FormField>
              </div>
            </details>
          </>
        )}
        {executionKind === "script" && (
          <>
            <FormField
              label={t("admin.tasks.execution.command")}
              required
              hint={t("admin.tasks.execution.commandHint")}
            >
              <TextInput
                aria-label={t("admin.tasks.execution.command")}
                value={scriptCommand}
                onChange={(event) => setScriptCommand(event.target.value)}
                placeholder={t("admin.tasks.execution.commandPlaceholder")}
                className="font-mono"
              />
            </FormField>
            <details className="rounded-md border border-dosia-border px-3 py-2">
              <summary className="cursor-pointer select-none text-sm text-dosia-text-secondary">
                {t("admin.tasks.execution.moreSettings.script")}
              </summary>
              <div className="mt-3 space-y-4">
                <FormField label={t("admin.tasks.execution.cwd")}>
                  <TextInput
                    aria-label={t("admin.tasks.execution.cwd")}
                    value={scriptCwd}
                    onChange={(event) => setScriptCwd(event.target.value)}
                    className="font-mono"
                  />
                </FormField>
                <FormField label={t("admin.tasks.execution.timeoutMs")}>
                  <TextInput
                    aria-label={t("admin.tasks.execution.timeoutMs")}
                    type="number"
                    min="1"
                    step="1"
                    value={scriptTimeoutMs}
                    onChange={(event) => setScriptTimeoutMs(event.target.value)}
                  />
                </FormField>
                <BridgeConnectorFields
                  mode={scriptConnectorMode}
                  names={scriptConnectorNames}
                  policy={scriptConnectorPolicy}
                  hint={t("admin.tasks.execution.scriptConnectorHint")}
                  onModeChange={setScriptConnectorMode}
                  onNamesChange={setScriptConnectorNames}
                  onPolicyChange={setScriptConnectorPolicy}
                />
              </div>
            </details>
          </>
        )}
        <FormField label={t("admin.tasks.trigger.kindLabel")} required>
          <Select
            aria-label={t("admin.tasks.trigger.kindLabel")}
            value={triggerKind}
            onChange={(event) => {
              const value = event.target.value;
              if (value === "cron" || value === "every" || value === "at") setTriggerKind(value);
            }}
          >
            <option value="cron">{t("admin.tasks.trigger.cron")}</option>
            <option value="every">{t("admin.tasks.trigger.every")}</option>
            <option value="at">{t("admin.tasks.trigger.at")}</option>
          </Select>
        </FormField>
        {triggerKind === "cron" && (
          <FormField
            label={t("admin.tasks.trigger.cronLabel")}
            required
            error={cronExpr ? cronErr ?? undefined : undefined}
            hint={cronErr ? undefined : `将按「${describeCron(cronExpr)}」触发（时区 ${cronTz}）`}
          >
            <TextInput
              value={cronExpr}
              onChange={(e) => setCronExpr(e.target.value)}
              placeholder="分 时 日 月 周，例：0 9 * * 1（每周一 09:00）"
              className="font-mono"
            />
          </FormField>
        )}
        {triggerKind === "every" && (
          <FormField
            label={t("admin.tasks.trigger.everyLabel")}
            required
            hint={t("admin.tasks.trigger.everyHint", { minutes: everyMinutes || "—" })}
          >
            <TextInput
              aria-label={t("admin.tasks.trigger.everyLabel")}
              type="number"
              min="1"
              step="1"
              value={everyMinutes}
              onChange={(event) => setEveryMinutes(event.target.value)}
            />
          </FormField>
        )}
        {triggerKind === "at" && (
          <FormField
            label={t("admin.tasks.trigger.atLabel")}
            required
            hint={t("admin.tasks.trigger.atHint")}
          >
            <TextInput
              aria-label={t("admin.tasks.trigger.atLabel")}
              type="datetime-local"
              value={atLocal}
              onChange={(event) => setAtLocal(event.target.value)}
            />
          </FormField>
        )}
        <FormField label="派给">
          <div className="space-y-2">
            <div className="flex gap-4 text-sm">
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="radio"
                  name="target-kind"
                  checked={targetKind === "all"}
                  onChange={() => setTargetKind("all")}
                  className="accent-dosia-accent"
                />
                <span>全员</span>
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="radio"
                  name="target-kind"
                  checked={targetKind === "users"}
                  onChange={() => setTargetKind("users")}
                  className="accent-dosia-accent"
                />
                <span>具体员工</span>
              </label>
            </div>
            {targetKind === "users" && (
              <div className="max-h-48 overflow-auto rounded-md border border-dosia-border p-2 space-y-1">
                {usersQ.loading && !usersQ.data ? (
                  <p className="text-xs text-dosia-text-dim">加载员工列表…</p>
                ) : (usersQ.data?.rows ?? []).length === 0 ? (
                  <p className="text-xs text-dosia-text-dim">暂无可选员工</p>
                ) : (
                  (usersQ.data?.rows ?? []).map((u: PublicUser) => (
                    <label
                      key={u.id}
                      className="flex items-center gap-2 text-sm cursor-pointer px-1 py-0.5 rounded hover:bg-dosia-bg-lighter"
                    >
                      <input
                        type="checkbox"
                        checked={selectedUsers.includes(u.user)}
                        onChange={() => toggleUser(u.user)}
                        className="accent-dosia-accent"
                      />
                      <span>{u.user}</span>
                      {u.role && (
                        <span className="text-xs text-dosia-text-dim">{u.role}</span>
                      )}
                    </label>
                  ))
                )}
              </div>
            )}
          </div>
        </FormField>
      </div>
    </Modal>
  );
}

// ---------- 执行历史抽屉 ----------

function ExecutionStatusPill({
  status,
}: {
  status: EnterpriseTaskExecution["status"];
}): JSX.Element {
  const { t } = useTranslation();
  const map: Record<
    EnterpriseTaskExecution["status"],
    { tone: "active" | "suspended" | "pending" | "disabled"; label: string }
  > = {
    queued: { tone: "pending", label: "排队中" },
    running: { tone: "pending", label: "运行中" },
    success: { tone: "active", label: "成功" },
    failed: { tone: "suspended", label: "失败" },
    overdue: { tone: "disabled", label: "超时未报" },
    // B-124：前置检查说没有新活，这一轮没唤醒 AI——灰色，不是失败。
    skipped: { tone: "disabled", label: t("admin.tasks.executionStatus.skipped") },
  };
  const { tone, label } = map[status];
  return <StatusPill status={tone} label={label} />;
}

function ExecutionsDrawer({
  orgCode,
  task,
  onClose,
}: {
  orgCode: string;
  task: EnterpriseTask;
  onClose: () => void;
}): JSX.Element {
  const listQ = useApiQuery<ExecutionsListResponse>(
    taskExecutionsPath(orgCode, { taskId: task.taskId, limit: 50 }),
    [orgCode, task.taskId],
  );
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const detailQ = useApiQuery<EnterpriseTaskExecution>(
    selectedId ? taskExecutionByIdPath(orgCode, selectedId) : null,
    [orgCode, selectedId],
  );
  const items = listQ.data?.items ?? [];

  return (
    <Drawer open onClose={onClose} title={`执行历史 · ${task.title}`} width={560}>
      {listQ.error && <ErrorBanner>{listQ.error.message}</ErrorBanner>}
      {listQ.loading && items.length === 0 ? (
        <p className="text-sm text-dosia-text-dim">加载中…</p>
      ) : items.length === 0 ? (
        <Empty title="暂无执行记录" />
      ) : (
        <div className="space-y-2">
          {items.map((ex) => (
            <div key={ex.executionId} className="rounded-lg border border-dosia-border">
              <button
                type="button"
                onClick={() =>
                  setSelectedId((cur) => (cur === ex.executionId ? null : ex.executionId))
                }
                className="w-full text-left p-3 space-y-1 hover:bg-dosia-bg-lighter"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-sm">{ex.userId}</span>
                  <ExecutionStatusPill status={ex.status} />
                </div>
                <div className="text-xs text-dosia-text-secondary">
                  {formatTime(ex.finishedAt ?? ex.startedAt ?? ex.scheduledAt)}
                </div>
                {ex.summary && (
                  <div className="text-xs text-dosia-text-secondary line-clamp-2">
                    {ex.summary}
                  </div>
                )}
              </button>
              {selectedId === ex.executionId && (
                <div className="border-t border-dosia-border p-3 space-y-2 text-xs">
                  {detailQ.loading ? (
                    <p className="text-dosia-text-dim">加载详情…</p>
                  ) : detailQ.error ? (
                    <ErrorBanner>{detailQ.error.message}</ErrorBanner>
                  ) : (
                    <>
                      <div className="grid grid-cols-2 gap-1 text-dosia-text-secondary">
                        <span>员工：{detailQ.data?.userId ?? ex.userId}</span>
                        <span>调度：{formatTime(detailQ.data?.scheduledAt)}</span>
                        <span>开始：{formatTime(detailQ.data?.startedAt)}</span>
                        <span>结束：{formatTime(detailQ.data?.finishedAt)}</span>
                      </div>
                      {(detailQ.data?.attempts ?? 0) > 0 && (
                        <div className="text-dosia-text-secondary">
                          尝试次数：{detailQ.data?.attempts}/{ENTERPRISE_TASK_MAX_ATTEMPTS}
                          {detailQ.data?.nextRetryAt
                            ? ` · 下次 ${formatTime(detailQ.data.nextRetryAt)}`
                            : ""}
                        </div>
                      )}
                      {(detailQ.data?.actualModel || detailQ.data?.fallbackReason) && (
                        <div className="text-dosia-text-secondary">
                          {detailQ.data?.actualModel
                            ? `实际模型：${detailQ.data.actualModel}`
                            : "模型归因"}
                          {detailQ.data?.fallbackReason ? ` · ${detailQ.data.fallbackReason}` : ""}
                        </div>
                      )}
                      {detailQ.data?.errorMessage && (
                        <div className="rounded bg-amber-500/10 border border-amber-500/30 p-2 text-amber-800">
                          {detailQ.data.errorMessage}
                        </div>
                      )}
                      {detailQ.data?.summary && (
                        <div className="whitespace-pre-wrap">{detailQ.data.summary}</div>
                      )}
                      {detailQ.data?.artifacts && detailQ.data.artifacts.length > 0 && (
                        <div className="space-y-1">
                          <p className="text-dosia-text-dim">交付文件</p>
                          {detailQ.data.artifacts.map((art) => {
                            const size = formatBytes(art.sizeBytes);
                            return (
                              <div
                                key={art.blobKey}
                                className="rounded-lg border border-dosia-border bg-dosia-bg-lighter px-2.5 py-1.5 text-dosia-text"
                              >
                                {art.filename}
                                {size ? <span className="ml-2 text-dosia-text-dim">{size}</span> : null}
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {detailQ.data?.warnings && detailQ.data.warnings.length > 0 && (
                        <div className="rounded bg-amber-500/10 border border-amber-500/30 p-2 text-amber-800 space-y-1">
                          <p className="font-medium">配置提醒</p>
                          {detailQ.data.warnings.map((w, i) => (
                            <p key={i}>{w.message}</p>
                          ))}
                        </div>
                      )}
                      {detailQ.data?.agentLog && detailQ.data.agentLog.length > 0 && (
                        <details>
                          <summary className="cursor-pointer select-none text-dosia-text-secondary">
                            完整执行日志（{detailQ.data.agentLog.length} 步）
                          </summary>
                          <pre className="mt-1 max-h-64 overflow-auto rounded bg-dosia-bg-lighter p-2 whitespace-pre-wrap break-all">
                            {JSON.stringify(detailQ.data.agentLog, null, 2)}
                          </pre>
                        </details>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Drawer>
  );
}

// ---------- 页面 ----------

export function EnterpriseTasksPanel(): JSX.Element {
  const { t } = useTranslation();
  const { org } = useOutletContext<OrgDetailContext>();
  const auth = useAtomValue(authAtom);
  // org-admin 管自己 org，super/support 代任意组织建/改任务（canManageOrg 集中派生）。
  const canManage = canManageOrg(auth?.scope);
  const tasksQ = useApiQuery<TasksListResponse>(
    canManage ? tasksPath(org.code) : null,
    [org.code, canManage],
  );
  const [dialog, setDialog] = useState<DialogState | null>(null);
  const [drawerTask, setDrawerTask] = useState<EnterpriseTask | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<EnterpriseTask | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const tasks = tasksQ.data?.items ?? [];

  const patchStatus = async (
    task: EnterpriseTask,
    status: EnterpriseTaskStatus,
  ): Promise<void> => {
    if (!auth) return;
    setBusyId(task.taskId);
    setActionError(null);
    try {
      await apiCall("PATCH", taskByIdPath(org.code, task.taskId), {
        token: auth.token,
        body: { status, expectedVersion: task.version },
      });
      await tasksQ.refetch();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setActionError("该任务已被他处修改，请刷新后重试。");
        await tasksQ.refetch();
      } else {
        setActionError(err instanceof ApiError ? err.message : "操作失败");
      }
    } finally {
      setBusyId(null);
    }
  };

  const deleteTask = async (task: EnterpriseTask): Promise<void> => {
    if (!auth) return;
    setBusyId(task.taskId);
    setActionError(null);
    try {
      await apiCall("DELETE", taskByIdPath(org.code, task.taskId), {
        token: auth.token,
      });
      await tasksQ.refetch();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setActionError(err.message || "该任务当前不支持删除");
      } else {
        setActionError(err instanceof ApiError ? err.message : "删除失败");
      }
    } finally {
      setBusyId(null);
      setConfirmDelete(null);
    }
  };

  const runNow = async (task: EnterpriseTask): Promise<void> => {
    if (!auth) return;
    setBusyId(task.taskId);
    setActionError(null);
    try {
      await apiCall("POST", taskRunNowPath(org.code, task.taskId), {
        token: auth.token,
      });
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : "触发失败");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold">自动化任务</h2>
          <p className="text-sm text-dosia-text-secondary">
            {t("admin.tasks.execution.panelSubtitle")}
          </p>
        </div>
        {canManage && !tasksQ.data?.channelDisabled && (
          <PrimaryButton onClick={() => setDialog({ mode: "create" })}>
            新建任务
          </PrimaryButton>
        )}
      </div>

      {!canManage ? (
        <div className="rounded-lg border border-dosia-border bg-dosia-bg-lighter p-4 text-sm text-dosia-text-secondary">
          请先登录管理员账号后管理自动化任务。
        </div>
      ) : tasksQ.data?.channelDisabled ? (
        <div className="rounded-lg border border-dosia-border bg-dosia-bg-lighter p-4 text-sm text-dosia-text-secondary">
          自动化任务通道未配置，请联系 DOSIA 运营。
        </div>
      ) : (
        <>
          {actionError && (
            <ErrorBanner onDismiss={() => setActionError(null)}>{actionError}</ErrorBanner>
          )}
          {tasksQ.error && <ErrorBanner>{tasksQ.error.message}</ErrorBanner>}
          {tasksQ.loading && tasks.length === 0 ? (
            <p className="text-sm text-dosia-text-dim">加载中…</p>
          ) : tasks.length === 0 ? (
            <Empty
              title="暂无自动化任务"
              description={t("admin.tasks.execution.emptyDescription")}
            />
          ) : (
            <div className="space-y-3">
              {tasks.map((task) => (
                <div
                  key={task.taskId}
                  className="rounded-lg border border-dosia-border p-4 space-y-2"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{task.title}</span>
                        <StatusPill
                          status={STATUS_TONE[task.status].tone}
                          label={STATUS_TONE[task.status].label}
                        />
                      </div>
                      <div className="text-xs text-dosia-text-secondary mt-0.5">
                        {triggerSummary(task.trigger)} · 目标 {targetSummary(task.target)} · v
                        {task.version}
                      </div>
                      <div className="text-xs text-dosia-text-secondary mt-0.5">
                        执行：{executionKindSummary(task.execution)} · 连接器：
                        {connectorSummary(task.execution)} · 应用工具：
                        {appToolSummary(task.execution)} · 单次封顶：{budgetSummary(task.execution)}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {task.status !== "archived" && (
                        <SecondaryButton
                          disabled={busyId === task.taskId}
                          onClick={() => void runNow(task)}
                        >
                          立即执行
                        </SecondaryButton>
                      )}
                      {task.status === "active" && (
                        <SecondaryButton
                          disabled={busyId === task.taskId}
                          onClick={() => void patchStatus(task, "paused")}
                        >
                          暂停
                        </SecondaryButton>
                      )}
                      {task.status === "paused" && (
                        <SecondaryButton
                          disabled={busyId === task.taskId}
                          onClick={() => void patchStatus(task, "active")}
                        >
                          恢复
                        </SecondaryButton>
                      )}
                      {task.status !== "archived" && (
                        <>
                          <SecondaryButton
                            disabled={busyId === task.taskId}
                            onClick={() => setDialog({ mode: "edit", task })}
                          >
                            编辑
                          </SecondaryButton>
                          <SecondaryButton
                            disabled={busyId === task.taskId}
                            onClick={() => void patchStatus(task, "archived")}
                          >
                            下线
                          </SecondaryButton>
                        </>
                      )}
                      <SecondaryButton
                        disabled={busyId === task.taskId}
                        onClick={() => setConfirmDelete(task)}
                      >
                        删除
                      </SecondaryButton>
                      <SecondaryButton onClick={() => setDrawerTask(task)}>
                        执行历史
                      </SecondaryButton>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {dialog && auth && (
        <TaskFormDialog
          orgCode={org.code}
          token={auth.token}
          state={dialog}
          onClose={() => setDialog(null)}
          onSaved={() => {
            setDialog(null);
            void tasksQ.refetch();
          }}
        />
      )}

      {drawerTask && (
        <ExecutionsDrawer
          orgCode={org.code}
          task={drawerTask}
          onClose={() => setDrawerTask(null)}
        />
      )}

      {confirmDelete ? (
        <Modal
          open
          onClose={() => setConfirmDelete(null)}
          title="彻底删除这条任务？"
          footer={
            <>
              <SecondaryButton onClick={() => setConfirmDelete(null)}>取消</SecondaryButton>
              <SecondaryButton
                danger
                disabled={busyId === confirmDelete.taskId}
                onClick={() => void deleteTask(confirmDelete)}
              >
                删除
              </SecondaryButton>
            </>
          }
        >
          <p className="text-sm text-dosia-text">
            「{confirmDelete.title}」删除后不可恢复，历史执行记录一并清除；只是暂时不跑请用「下线」。
          </p>
        </Modal>
      ) : null}
    </div>
  );
}
